// src/components/VideoGallery.jsx
import React, { useMemo, useState, useCallback, useEffect } from 'react';
const img = (p) => process.env.PUBLIC_URL + `/img/${p}`;

/** Extrait un ID YouTube depuis un lien (youtu.be, youtube.com/watch?v=, /shorts/, etc.) */
function parseYouTubeId(url) {
    try {
        const u = new URL(url);
        if (u.hostname.includes('youtu.be')) return u.pathname.slice(1);
        if (u.searchParams.get('v')) return u.searchParams.get('v');
        const parts = u.pathname.split('/');
        const shortsIdx = parts.findIndex(p => p === 'shorts');
        if (shortsIdx >= 0 && parts[shortsIdx + 1]) return parts[shortsIdx + 1];
    } catch { }
    return null;
}

function ytEmbed(id) {
    return `https://www.youtube-nocookie.com/embed/${id}?rel=0&modestbranding=1&autoplay=1`;
}

/** Candidats de vignettes (du + grand au + petit) */
function thumbCandidates(id) {
    return [
        // webp hi-res (plus léger, si dispo)
        `https://i.ytimg.com/vi_webp/${id}/maxresdefault.webp`,
        `https://i.ytimg.com/vi/${id}/maxresdefault.jpg`,
        `https://i.ytimg.com/vi_webp/${id}/sddefault.webp`,
        `https://i.ytimg.com/vi/${id}/sddefault.jpg`,
        `https://i.ytimg.com/vi_webp/${id}/hqdefault.webp`,
        `https://i.ytimg.com/vi/${id}/hqdefault.jpg`,
        `https://i.ytimg.com/vi/${id}/mqdefault.jpg`,
    ];
}

/** Image avec fallback automatique entre les URLs candidates */
function SmartThumb({
    id,
    alt = '',
    // hint CLS (facultatif mais utile)
    width = 1280,
    height = 720,
    className,
    sizes, // ex: "(max-width: 1080px) 92vw, 980px"
}) {
    const candidates = useMemo(() => thumbCandidates(id), [id]);
    const [idx, setIdx] = useState(0);

    // srcSet (on ne sait pas à l'avance lesquelles 404, mais ça donne des hints au navigateur)
    // On met seulement les plus probables pour éviter des requêtes inutiles
    const probableSrcSet = useMemo(
        () =>
            [
                `https://i.ytimg.com/vi/${id}/maxresdefault.jpg 1280w`,
                `https://i.ytimg.com/vi/${id}/sddefault.jpg 640w`,
                `https://i.ytimg.com/vi/${id}/hqdefault.jpg 480w`,
            ].join(', '),
        [id]
    );

    const onError = () => {
        setIdx((i) => (i < candidates.length - 1 ? i + 1 : i));
    };

    const src = candidates[idx];

    return (
        <img
            className={className}
            src={src}
            srcSet={probableSrcSet}
            sizes={sizes}
            alt={alt}
            onError={onError}
            width={width}
            height={height}
            decoding="async"
            loading="lazy"
            style={{ display: 'block', width: '100%', height: '100%', objectFit: 'cover' }}
        />
    );
}

export default function VideoGallery({
    title = 'TRAILER & VIDEOS',
    videos = [],
    channelUrl,
}) {
    const items = useMemo(() => {
        return videos
            .map((v) => {
                const id = parseYouTubeId(v.url);
                if (!id) return null;
                return {
                    id,
                    title: v.title || 'Video',
                    url: v.url,
                    embed: ytEmbed(id),
                    featured: !!v.featured,
                };
            })
            .filter(Boolean);
    }, [videos]);

    // Met le trailer (featured) en premier
    const ordered = useMemo(() => {
        const feat = items.find((i) => i.featured) || items[0];
        const rest = items.filter((i) => i !== feat);
        return feat ? [feat, ...rest] : rest;
    }, [items]);

    const [openIndex, setOpenIndex] = useState(-1);
    const close = useCallback(() => setOpenIndex(-1), []);
    const prev = useCallback(() => setOpenIndex((i) => (i <= 0 ? ordered.length - 1 : i - 1)), [ordered.length]);
    const next = useCallback(() => setOpenIndex((i) => (i >= ordered.length - 1 ? 0 : i + 1)), [ordered.length]);

    // ESC / ← →
    useEffect(() => {
        const onKey = (e) => {
            if (e.key === 'Escape') close();
            if (e.key === 'ArrowLeft') prev();
            if (e.key === 'ArrowRight') next();
        };
        document.addEventListener('keydown', onKey);
        return () => document.removeEventListener('keydown', onKey);
    }, [close, prev, next]);

    return (
        <section id="videos" className="rl-videos">
            <img className="rl-sectionBg bg3" src={img('bg2.png')} alt="" aria-hidden="true" />

            <div className="rl-videos__inner">
                <h2 className="rl-videos__title">{title}</h2>

                {ordered.length > 0 && (
                    <div
                        className="rl-videos__featured"
                        onClick={() => setOpenIndex(0)}
                        role="button"
                        tabIndex={0}
                        onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && setOpenIndex(0)}
                    >
                        <div className="rl-videos__thumb">
                            <SmartThumb
                                id={ordered[0].id}
                                alt=""
                                width={1280}
                                height={720}
                                sizes="(max-width: 1080px) 92vw, 980px"
                            />
                            <span className="rl-videos__play" aria-hidden="true">▶</span>
                        </div>
                        <h3 className="rl-videos__caption">{ordered[0].title}</h3>
                    </div>
                )}

                {ordered.length > 1 && (
                    <div className="rl-videos__grid">
                        {ordered.slice(1).map((it, idx) => (
                            <article
                                key={it.id}
                                className="rl-videos__card"
                                onClick={() => setOpenIndex(idx + 1)}
                                role="button"
                                tabIndex={0}
                                onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && setOpenIndex(idx + 1)}
                            >
                                <div className="rl-videos__thumb">
                                    <SmartThumb
                                        id={it.id}
                                        alt=""
                                        width={640}
                                        height={360}
                                        sizes="(max-width: 1080px) 46vw, 320px"
                                    />
                                    <span className="rl-videos__play" aria-hidden="true">▶</span>
                                </div>
                                <h4 className="rl-videos__caption">{it.title}</h4>
                            </article>
                        ))}
                    </div>
                )}

                <div className="rl-videos__footer">
                    {channelUrl && (
                        <a className="rl-btn rl-btn--ghost" href={channelUrl} target="_blank" rel="noopener noreferrer">
                            See all videos on YouTube
                        </a>
                    )}
                    <a className="rl-btn rl-btn--primary" target="_blank" href="https://discord.gg/RmubB8KhRW">PLAY NOW</a>
                </div>
            </div>

            {/* MODAL LECTEUR */}
            {openIndex >= 0 && ordered[openIndex] && (
                <div className="rl-videomodal" role="dialog" aria-modal="true" aria-label="Video player">
                    <div className="rl-videomodal__scrim" onClick={close} />
                    <div className="rl-videomodal__panel">
                        <button className="rl-videomodal__close" onClick={close} aria-label="Close">×</button>
                        <div className="rl-videomodal__frame">
                            <iframe
                                title={ordered[openIndex].title}
                                src={ordered[openIndex].embed}
                                frameBorder="0"
                                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                                allowFullScreen
                            />
                        </div>
                        <footer className="rl-videomodal__footer">
                            <div className="rl-videos__nav">
                                <button type="button" className="rl-btn rl-btn--ghost" onClick={prev} aria-label="Previous">◀</button>
                                <button type="button" className="rl-btn rl-btn--ghost" onClick={next} aria-label="Next">▶</button>
                            </div>
                            <a className="rl-btn rl-btn--ghost" href={ordered[openIndex].url} target="_blank" rel="noopener noreferrer">
                                Open on YouTube
                            </a>
                        </footer>
                    </div>
                </div>
            )}
        </section>
    );
}
